<template>
  <div class="max-w-6xl mx-auto mt-8 space-y-6">
    <div class="flex items-start justify-between bg-white p-6 rounded-xl shadow-md">
      <div>
        <h2 class="text-2xl font-bold text-gray-800 flex items-center gap-2">
          Team Templates
          <BaseInfoIcon :text="ERKLAERUNG.teamTemplates" label="Team Templates" />
        </h2>
        <p class="text-gray-600 text-sm mt-1">
          Create reusable team configurations with users and roles to quickly set up sessions.
        </p>
      </div>
      <router-link to="/sessions/assign-teams">
        <BaseButton class="bg-blue-500 hover:bg-blue-600">Assign to Sessions</BaseButton>
      </router-link>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Builder -->
      <div class="bg-white p-6 rounded-xl shadow-md space-y-4 lg:col-span-2">
        <h3 class="text-lg font-semibold text-gray-800">{{ editingId ? 'Edit template' : 'Create template' }}</h3>

        <div class="space-y-3">
          <BaseInput v-model="form.name" placeholder="Template name" />
          <textarea
            v-model="form.description"
            rows="3"
            class="w-full border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring"
            placeholder="Description (optional)"
          ></textarea>
        </div>

        <div class="bg-gray-50 border rounded-lg p-4 space-y-3">
          <div class="flex flex-col gap-3 md:flex-row md:items-start">
            <BaseSelect
              v-model="form.selectedUserId"
              :options="userOptions"
              placeholder="Select user"
              :showPlaceholder="true"
              class="flex-1"
            />
            
            <!-- Multiple Team Selection with Dropdown -->
            <BaseMultiSelect
              v-model="form.selectedTeamIds"
              :options="teamOptions"
              placeholder="Select teams"
              class="flex-1"
            />
            
            <BaseSelect
              v-model="form.selectedRoleId"
              :options="roleOptions"
              placeholder="Select role"
              :showPlaceholder="true"
              class="flex-1"
            />
            <BaseButton class="md:self-start" @click="addEntry">Add</BaseButton>
          </div>

          <p class="text-xs text-gray-600">Add each user with one or more teams and a role for this template.</p>

          <div v-if="form.entries.length" class="overflow-x-auto">
            <table class="min-w-full text-sm text-left text-gray-700">
              <thead>
                <tr class="text-gray-500">
                  <th class="py-2">User</th>
                  <th class="py-2">Teams</th>
                  <th class="py-2">Role</th>
                  <th class="py-2 w-12"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="entry in form.entries" :key="entry.id" class="border-t">
                  <td class="py-2">{{ userLabel(entry.userId) }}</td>
                  <td class="py-2">
                    <span v-if="entry.teamIds && entry.teamIds.length">
                      {{ entry.teamIds.map(tid => teamLabel(tid)).join(', ') }}
                    </span>
                    <span v-else>—</span>
                  </td>
                  <td class="py-2">{{ roleLabel(entry.roleId) || '—' }}</td>
                  <td class="py-2 text-right">
                    <BaseButton variant="delete" class="p-2 rounded-full" @click="removeEntry(entry.id)">
                      <TrashIcon class="h-5 w-5" />
                    </BaseButton>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-sm text-gray-500">No users added yet.</p>
        </div>

        <div class="flex justify-end gap-2">
          <BaseButton variant="cancel" @click="resetForm">{{ editingId ? 'Cancel' : 'Reset' }}</BaseButton>
          <BaseButton @click="saveConstellation">{{ editingId ? 'Update' : 'Save template' }}</BaseButton>
        </div>
      </div>

      <!-- Existing constellations -->
      <div class="bg-white p-6 rounded-xl shadow-md space-y-4 lg:col-span-1">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-800">Saved templates</h3>
        </div>

        <ul class="divide-y divide-gray-200 max-h-[600px] overflow-y-auto" v-if="constellations.length">
          <li v-for="c in constellations" :key="c.id" class="py-3">
            <div class="flex justify-between items-start gap-2">
              <div @click="selectConstellation(c.id)" class="cursor-pointer flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="font-medium text-gray-900 truncate" :class="{ 'text-indigo-600': selectedConstellationId === c.id }">{{ c.name }}</span>
                  <span class="text-xs text-gray-500 flex-shrink-0">({{ c.entries.length }})</span>
                </div>
                <p class="text-xs text-gray-600 truncate" v-if="c.description">{{ c.description }}</p>
              </div>
              <div class="flex gap-1 flex-shrink-0">
                <BaseButton
                  class="p-1.5 rounded-full bg-blue-500 hover:bg-blue-600"
                  tooltip="Edit template"
                  @click="loadForEdit(c)"
                >
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </BaseButton>
                <BaseButton
                  class="p-1.5 rounded-full"
                  variant="delete"
                  tooltip="Delete template"
                  @click="deleteConstellation(c.id)"
                >
                  <TrashIcon class="h-4 w-4" />
                </BaseButton>
              </div>
            </div>
          </li>
        </ul>
        <p v-else class="text-sm text-gray-500">No templates yet. Create one on the left.</p>
      </div>
    </div>

    <!-- Selected constellation details -->
    <div v-if="selectedConstellation" class="bg-white p-6 rounded-xl shadow-md space-y-4">
      <div class="flex items-center justify-between">
        <h3 class="text-lg font-semibold text-gray-800">Teams in "{{ selectedConstellation.name }}"</h3>
        <span class="text-sm text-gray-500">{{ constellationTeams.length }} teams</span>
      </div>

      <div v-if="constellationTeams.length" class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="team in constellationTeams"
          :key="team.teamId"
          class="border rounded-xl p-4 bg-gray-50 shadow-sm"
        >
          <div class="flex items-center justify-between mb-3">
            <div>
              <p class="font-semibold text-gray-900">{{ team.teamName }}</p>
              <p class="text-xs text-gray-500">{{ team.users.length }} members</p>
            </div>
            <div class="h-8 w-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-semibold">
              {{ team.users.length }}
            </div>
          </div>

          <div v-if="team.users.length" class="space-y-2">
            <div
              v-for="user in team.users"
              :key="user.userId"
              class="flex items-center justify-between rounded-lg bg-white border px-3 py-2"
            >
              <div class="flex items-center gap-2 text-gray-800">
                <span class="font-medium">{{ userLabel(user.userId) }}</span>
              </div>
              <span
                class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
                :class="user.roleId ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-500'"
              >
                {{ roleLabel(user.roleId) || 'No role' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="usersWithoutTeam.length" class="border rounded-xl p-4 bg-gray-50 shadow-sm">
        <div class="flex items-center justify-between mb-3">
          <div>
            <p class="font-semibold text-gray-900">No Team</p>
            <p class="text-xs text-gray-500">{{ usersWithoutTeam.length }} users</p>
          </div>
        </div>

        <div class="space-y-2">
          <div
            v-for="user in usersWithoutTeam"
            :key="user.userId"
            class="flex items-center justify-between rounded-lg bg-white border px-3 py-2"
          >
            <div class="flex items-center gap-2 text-gray-800">
              <span class="font-medium">{{ userLabel(user.userId) }}</span>
            </div>
            <span
              class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
              :class="user.roleId ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-500'"
            >
              {{ roleLabel(user.roleId) || 'No role' }}
            </span>
          </div>
        </div>
      </div>

      <p v-if="!constellationTeams.length && !usersWithoutTeam.length" class="text-sm text-gray-500">No users in this template.</p>
    </div>
  </div>
</template>

<script>
import BaseButton from "@/BaseComponents/BaseButton.vue";
import BaseInfoIcon from "@/BaseComponents/BaseInfoIcon.vue";
import BaseInput from "@/BaseComponents/BaseInput.vue";
import BaseSelect from "@/BaseComponents/BaseSelect.vue";
import BaseMultiSelect from "@/BaseComponents/BaseMultiSelect.vue";
import { ERKLAERUNG } from "@/async-sessions/erklaerungen";
import { TrashIcon } from "@heroicons/vue/24/solid";

import { getTeams } from "@/live-sessions/api/teams";
import { getStudentUsers } from "@/api/users";
import { getRoles } from "@/live-sessions/api/roles";
import {
  listTeamConstellations,
  createTeamConstellation,
  updateTeamConstellation,
  deleteTeamConstellation,
} from "@/live-sessions/api/team_templates";

export default {
  components: {
    BaseButton,
    BaseInfoIcon,
    BaseInput,
    BaseSelect,
    BaseMultiSelect,
    TrashIcon,
  },

  data() {
    return {
      ERKLAERUNG,
      teams: [],
      users: [],
      roles: [],
      constellations: [],
      openConstellationId: null,
      selectedConstellationId: null,
      editingId: null,
      form: {
        name: "",
        description: "",
        entries: [],
        selectedUserId: null,
        selectedTeamIds: [],  // Changed to array for multiple teams
        selectedRoleId: null,
      },
    };
  },

  computed: {
    userOptions() {
      const addedUserIds = this.form.entries.map(e => e.userId);
      return this.users
        .filter(u => !addedUserIds.includes(u.id))
        .map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` }));
    },
    teamOptions() {
      return this.teams.map(t => ({ value: t.id, label: t.name }));
    },
    roleOptions() {
      return [{ value: null, label: "No role" }, ...this.roles.map(r => ({ value: r.id, label: r.name }))];
    },
    selectedConstellation() {
      return this.constellations.find(c => c.id === this.selectedConstellationId) || null;
    },
    constellationTeams() {
      if (!this.selectedConstellation) return [];
      const teamGroups = {};
      this.selectedConstellation.entries.forEach(entry => {
        const teamIds = entry.teamIds || [];
        teamIds.forEach(teamId => {
          if (!teamGroups[teamId]) {
            teamGroups[teamId] = {
              teamId: teamId,
              teamName: this.teamLabel(teamId),
              users: []
            };
          }
          teamGroups[teamId].users.push(entry);
        });
      });
      return Object.values(teamGroups);
    },
    usersWithoutTeam() {
      if (!this.selectedConstellation) return [];
      return this.selectedConstellation.entries.filter(e => !e.teamIds || e.teamIds.length === 0);
    },
  },

  methods: {
    async fetchConstellations() {
      const res = await listTeamConstellations();
      const mapEntries = (entries = []) => entries.map(e => ({
        id: e.id,
        userId: Number(e.user_id),
        teamIds: (e.team_ids || []).map(id => Number(id)),
        roleId: e.role_id === null || e.role_id === undefined ? null : Number(e.role_id),
      }));

      this.constellations = (res.data || []).map(c => ({
        ...c,
        entries: mapEntries(c.entries),
      }));
    },

    userLabel(id) {
      const u = this.users.find(user => user.id === id);
      return u ? `${u.first_name} ${u.last_name}` : "Unknown user";
    },
    teamLabel(id) {
      const t = this.teams.find(team => team.id === id);
      return t ? t.name : "Unknown team";
    },
    roleLabel(id) {
      const r = this.roles.find(role => role.id === id);
      return r ? r.name : null;
    },

    addEntry() {
      if (!this.form.selectedUserId) return;
      const hasTeams = this.form.selectedTeamIds && this.form.selectedTeamIds.length > 0;
      const hasRole = this.form.selectedRoleId !== null && this.form.selectedRoleId !== undefined;
      if (!hasTeams && !hasRole) return; // need at least team or role

      const userId = Number(this.form.selectedUserId);
      const teamIds = hasTeams ? this.form.selectedTeamIds.map(id => Number(id)) : [];
      const roleId = hasRole ? Number(this.form.selectedRoleId) : null;

      const alreadyAdded = this.form.entries.some(e => e.userId === userId);
      if (alreadyAdded) return;

      this.form.entries.push({
        id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
        userId,
        teamIds,
        roleId,
      });

      this.form.selectedUserId = null;
      this.form.selectedTeamIds = [];
      this.form.selectedRoleId = null;
    },

    removeEntry(entryId) {
      this.form.entries = this.form.entries.filter(e => e.id !== entryId);
    },

    selectConstellation(id) {
      this.selectedConstellationId = id;
    },

    resetForm() {
      this.editingId = null;
      this.form = {
        name: "",
        description: "",
        entries: [],
        selectedUserId: null,
        selectedTeamIds: [],
        selectedRoleId: null,
      };
    },

    loadForEdit(constellation) {
      this.editingId = constellation.id;
      this.form.name = constellation.name;
      this.form.description = constellation.description || "";
      this.form.entries = constellation.entries.map(e => ({
        id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
        userId: e.userId,
        teamIds: e.teamIds || [],
        roleId: e.roleId,
      }));
      this.form.selectedUserId = null;
      this.form.selectedTeamIds = [];
      this.form.selectedRoleId = null;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    async saveConstellation() {
      if (!this.form.name.trim() || this.form.entries.length === 0) return;

      const payload = {
        name: this.form.name.trim(),
        description: this.form.description.trim() || null,
        entries: this.form.entries.map(e => ({
          user_id: e.userId,
          team_ids: e.teamIds || [],
          role_id: e.roleId,
        })),
      };

      if (this.editingId) {
        await updateTeamConstellation(this.editingId, payload);
      } else {
        await createTeamConstellation(payload);
      }
      await this.fetchConstellations();
      this.resetForm();
    },

    async deleteConstellation(id) {
      await deleteTeamConstellation(id);
      await this.fetchConstellations();
    },

    async fetchInitialData() {
      const [teamsRes, usersRes, rolesRes] = await Promise.all([
        getTeams(),
        getStudentUsers(),
        getRoles(),
      ]);
      this.teams = teamsRes.data || [];
      this.users = usersRes.data || [];
      this.roles = rolesRes.data || [];
    },
  },

  async mounted() {
    await Promise.all([this.fetchConstellations(), this.fetchInitialData()]);
  },
};
</script>
